import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Loading, ErrorState, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, ArrowLeft, Search, MessageSquare, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.patients || d?.items || d?.results || []);
const msgList = (d) => (Array.isArray(d) ? d : d?.items || d?.messages || d?.results || []);
const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export default function PhysioChat() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [errP, setErrP] = useState("");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingM, setLoadingM] = useState(false);
  const [errM, setErrM] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const bottomRef = useRef(null);

  const loadPatients = useCallback(async () => {
    setLoadingP(true); setErrP("");
    try { setPatients(asList(await api.patients())); }
    catch (e) { setErrP(errMsg(e)); }
    finally { setLoadingP(false); }
  }, []);

  const loadMessages = useCallback(async (pid) => {
    if (!pid) return;
    setLoadingM(true); setErrM("");
    try { setMessages(msgList(await api.conversation(pid))); }
    catch (e) { setErrM(errMsg(e)); }
    finally { setLoadingM(false); }
  }, []);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  useEffect(() => {
    const pid = params.get("patient");
    if (pid && patients.length && active?.id !== pid) {
      const p = patients.find((x) => x.id === pid);
      if (p) setActive(p);
    }
  }, [params, patients, active]);

  useEffect(() => { if (active?.id) loadMessages(active.id); }, [active, loadMessages]);

  useEffect(() => {
    if (!active?.id) return;
    const t = setInterval(() => loadMessages(active.id), 10000);
    return () => clearInterval(t);
  }, [active, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openPatient = (p) => { setActive(p); setParams({ patient: p.id }); };
  const back = () => { setActive(null); setParams({}); };

  const send = async () => {
    if (!text.trim() || !active?.id) return;
    setSending(true);
    try {
      await api.sendMessage({ receiver_id: active.id, content: text.trim() });
      setText(""); loadMessages(active.id);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  const filtered = patients.filter((p) => (p.full_name || p.username || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-3xl font-semibold text-foreground sm:text-4xl">Chat</h1>
        <p className="mt-1 text-sm text-muted-foreground">Messaggi con i tuoi pazienti.</p>
      </div>

      <div className="grid h-[74vh] gap-4 lg:grid-cols-[320px_1fr]">
        {/* Patient list */}
        <Card className={`flex-col overflow-hidden border-border/70 ${active ? "hidden lg:flex" : "flex"}`}>
          <div className="border-b border-border/60 bg-white p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca paziente..." className="pl-9" data-testid="chat-patient-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="chat-patient-list">
            {loadingP ? <Loading label="Caricamento pazienti..." /> : errP ? <ErrorState message={errP} onRetry={loadPatients} /> :
              filtered.length === 0 ? <EmptyState title="Nessun paziente" /> :
                filtered.map((p) => {
                  const name = p.full_name || p.username;
                  const unread = p.unread_messages || 0;
                  const isActive = active?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => openPatient(p)}
                      data-testid={`chat-patient-${p.id}`}
                      className={`flex w-full items-center gap-3 border-b border-border/40 px-4 py-3 text-left transition-colors ${isActive ? "bg-primary/10" : "hover:bg-muted/60"}`}
                    >
                      <Avatar className="h-10 w-10 bg-primary/15"><AvatarFallback className="bg-transparent text-sm font-semibold text-primary">{initials(name)}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#1f3a5f]">{name}</p>
                        <p className="truncate text-xs text-muted-foreground">@{p.username}</p>
                      </div>
                      {unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground" data-testid={`chat-unread-${p.id}`}>{unread}</span>}
                    </button>
                  );
                })}
          </div>
        </Card>

        {/* Conversation */}
        <Card className={`flex-col overflow-hidden border-border/70 ${active ? "flex" : "hidden lg:flex"}`}>
          {!active ? (
            <div className="grid h-full place-items-center p-8 text-center" data-testid="chat-empty">
              <div>
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary"><MessageSquare className="h-7 w-7" /></div>
                <p className="mt-4 font-semibold">Seleziona un paziente</p>
                <p className="mt-1 text-sm text-muted-foreground">Scegli una conversazione dall'elenco per iniziare a chattare.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-border/60 bg-white px-4 py-3">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={back} data-testid="chat-back-button"><ArrowLeft className="h-5 w-5" /></Button>
                <Avatar className="h-10 w-10 bg-primary/15"><AvatarFallback className="bg-transparent text-sm font-semibold text-primary">{initials(active.full_name || active.username)}</AvatarFallback></Avatar>
                <div>
                  <p className="font-bold text-[#1f3a5f]">{active.full_name || active.username}</p>
                  <p className="text-xs text-muted-foreground">@{active.username}</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 bg-[#fdf3d9] px-4 py-2 text-sm font-medium text-[#3f7a70]">
                <AlertTriangle className="h-4 w-4 text-[#d9a441]" /> Non usare la chat per emergenze mediche
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto bg-[#f2f6fb] p-4" data-testid="chat-messages">
                {loadingM ? <Loading label="Caricamento messaggi..." /> : errM ? <ErrorState message={errM} onRetry={() => loadMessages(active.id)} /> :
                  messages.length === 0 ? <p className="pt-10 text-center text-sm text-muted-foreground">Nessun messaggio. Scrivi il primo!</p> :
                    messages.map((m, i) => {
                      const mine = (m.sender_id || m.from_user_id || m.sender) === user.id;
                      return (
                        <div key={m.id || m._id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${mine ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md bg-white text-foreground"}`}>
                            <p className="whitespace-pre-line">{m.content || m.text || m.message}</p>
                            <p className={`mt-1 text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                              {new Date(m.created_at || m.timestamp || Date.now()).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                <div ref={bottomRef} />
              </div>

              <div className="flex items-center gap-2 border-t border-border/60 bg-white p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi un messaggio..." className="rounded-full" data-testid="chat-input" />
                <Button onClick={send} disabled={sending} size="icon" className="h-11 w-11 shrink-0 rounded-full" data-testid="chat-send-button"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
